/**
 * Hulasi Product — Main Section JS
 * Dawn 15.2.0 | OS 2.0
 * Iteration 5: Desktop motion system (scroll reveal + micro-interactions)
 */

(function () {
  'use strict';

  const SECTION_SELECTOR = '.hulasi-product';

  /* ===========================================================
     DESKTOP REVEAL SYSTEM
     Adds .is-inview to [data-hulasi-reveal] elements on scroll.
     Active only on desktop (≥ 990px) with no reduced-motion pref.
     =========================================================== */

  const HulasiReveal = (() => {
    const STAGGER_MS = 80;
    const OBSERVER_OPTS = { threshold: 0.12, rootMargin: '0px 0px -40px 0px' };
    let observer = null;

    function canAnimate() {
      return window.matchMedia('(min-width: 990px)').matches &&
             !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function getObserver() {
      if (!observer) {
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-inview');
              observer.unobserve(entry.target);
            }
          });
        }, OBSERVER_OPTS);
      }
      return observer;
    }

    function init(root) {
      if (!canAnimate()) return;
      const scope = root || document;
      const targets = scope.querySelectorAll('[data-hulasi-reveal]:not(.hulasi-reveal)');
      if (targets.length === 0) return;

      const obs = getObserver();

      targets.forEach((el) => {
        const delay = parseInt(el.dataset.hulasiReveal, 10);
        if (delay > 0) {
          el.style.setProperty('--hulasi-reveal-delay', (delay * STAGGER_MS) + 'ms');
        }

        /* Synchronous viewport check — elements already visible get
           both classes in the same paint frame so there is no flash. */
        const rect = el.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;

        if (inView) {
          el.classList.add('hulasi-reveal', 'is-inview');
        } else {
          el.classList.add('hulasi-reveal');
          obs.observe(el);
        }
      });
    }

    function cleanup(root) {
      if (!observer) return;
      const scope = root || document;
      scope.querySelectorAll('.hulasi-reveal').forEach((el) => {
        observer.unobserve(el);
        el.classList.remove('hulasi-reveal', 'is-inview');
        el.style.removeProperty('--hulasi-reveal-delay');
      });
    }

    return { init, cleanup };
  })();

  class HulasiProduct {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.productId = section.dataset.productId;
      this.productHandle = section.dataset.productHandle || '';
      this.isPersonalized = section.dataset.isPersonalized === 'true';
      this.totalMedia = parseInt(section.dataset.totalMedia, 10) || 0;

      /* ---- Gallery ---- */
      this.thumbnails = section.querySelectorAll('.hulasi-product__thumbnail');
      this.slides = section.querySelectorAll('.hulasi-product__media-slide');
      this.dots = section.querySelectorAll('.hulasi-product__media-dot');
      this.currentIndex = 0;
      this.imageSlideIndices = [];

      /* ---- Zoom ---- */
      this.zoomModal = section.querySelector('.hulasi-zoom-modal');
      this.zoomImage = section.querySelector('.hulasi-zoom-modal__image');
      this.zoomContainer = section.querySelector('.hulasi-zoom-modal__image-container');
      this.zoomClose = section.querySelector('.hulasi-zoom-modal__close');
      this.zoomPrev = section.querySelector('.hulasi-zoom-modal__nav-prev');
      this.zoomNext = section.querySelector('.hulasi-zoom-modal__nav-next');
      this.zoomCounter = section.querySelector('.hulasi-zoom-modal__counter');
      this.zoomBtn = section.querySelector(`#HulasiZoomBtn-${this.sectionId}`);
      this.zoomCurrentIndex = 0;
      this.zoomImages = [];
      this._zoomState = { scale: 1, x: 0, y: 0 };
      this._pinchState = {};
      this._panState = {};

      /* ---- Personalization ---- */
      this.personalizationContainer = section.querySelector('.hulasi-product__personalization');
      this.nameInput = section.querySelector(`#HulasiNameInput-${this.sectionId}`);
      this.hiddenNameInput = section.querySelector(`#HulasiPersonalisedName-${this.sectionId}`);
      this.charCount = section.querySelector(`#HulasiCharCount-${this.sectionId}`);
      this.errorEl = section.querySelector(`#HulasiNameError-${this.sectionId}`);
      this.previewText = section.querySelector(`#HulasiPreviewText-${this.sectionId}`);
      this.fontRadios = section.querySelectorAll('.hulasi-personalization__font-radio');
      this.fontOptions = section.querySelectorAll('.hulasi-personalization__font-option');
      this.langRadios = section.querySelectorAll('.hulasi-personalization__lang-radio');
      this.langBtns = section.querySelectorAll('.hulasi-personalization__toggle-btn');
      this._currentFont = 'Great Vibes';
      this._currentLang = 'en';
      this._rawInput = '';
      this._transliterateReady = false;
      this._errorMessages = {};

      /* ---- Form ---- */
      this.form = section.querySelector(`#product-form-${this.sectionId}`);
      this.returnToInput = section.querySelector(`#HulasiReturnTo-${this.sectionId}`);

      /* ---- Variants ---- */
      this.variantIdInput = section.querySelector(`#HulasiVariantId-${this.sectionId}`);
      this.variantSelects = section.querySelectorAll('.hulasi-product__option-select');
      this.variantsData = this._parseVariantsJson();

      /* ---- Sticky bar ---- */
      this.stickyBar = section.querySelector('.hulasi-product__sticky-bar');
      this.buyButtonsBlock = section.querySelector('.hulasi-product__block--buy-buttons');
      this.stickyATC = section.querySelector(`#HulasiStickyATC-${this.sectionId}`);
      this.stickyBuyNow = section.querySelector(`#HulasiStickyBuyNow-${this.sectionId}`);

      /* ---- Pincode ---- */
      this.pincodeContainer = section.querySelector('.hulasi-product__pincode');
      this.pincodeInput = section.querySelector(`#HulasiPincodeInput-${this.sectionId}`);
      this.pincodeBtn = section.querySelector(`#HulasiPincodeBtn-${this.sectionId}`);
      this.pincodeResult = section.querySelector(`#HulasiPincodeResult-${this.sectionId}`);
      this.pincodeLoading = section.querySelector(`#HulasiPincodeLoading-${this.sectionId}`);
      this._pincodeData = null;
      this._pincodeCacheKey = '';
      this._verifiedPostalPincodes = new Set();

      /* ---- Similar scroll ---- */
      this.similarBtn = section.querySelector(`#HulasiScrollToSimilar-${this.sectionId}`);

      /* ---- Share ---- */
      this.shareBtn = section.querySelector(`#HulasiShareBtn-${this.sectionId}`);
      this.shareModal = document.querySelector(`#HulasiShareModal-${this.sectionId}`);

      /* ---- Wishlist ---- */
      this.wishlistBtn = section.querySelector(`#HulasiWishlistBtn-${this.sectionId}`);

      /* ---- Rating pill ---- */
      this.ratingPill = section.querySelector(`#HulasiRatingPill-${this.sectionId}`);

      /* ---- Bound handlers ---- */
      this._onKeyDown = this._handleKeyDown.bind(this);

      this.init();
    }

    init() {
      this._setStickyOffset();
      this._collectImageSlides();
      this._bindThumbnails();
      this._injectSliderArrows();
      this._dynamicThumbnailFit();
      this._bindMobileSwipe();
      this._bindZoomBtn();
      this._bindZoomModalControls();
      this._bindPinchZoom();
      this._initStickyBarObserver();
      this._bindSimilarScroll();
      this._bindVariantChange();
      this._bindFormSubmit();
      this._bindStickyBarButtons();
      this._initPincodeChecker();
      this._initOffersAccordion();
      this._initRatingPill();
      this._initShareButton();
      this._initWishlistButton();

      if (this.isPersonalized) {
        this._initPersonalization();
      }

      this._initReveal();
    }

    /* ===========================================================
       SCROLL REVEAL (Desktop only)
       =========================================================== */

    _initReveal() {
      HulasiReveal.init(this.section);
    }

    /* ===========================================================
       STICKY OFFSET — Simplified & reliable
       =========================================================== */

    _setStickyOffset() {
      const mediaCol = this.section.querySelector('.hulasi-product__media-col');
      if (!mediaCol) return;

      const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

      const apply = () => {
        if (!isDesktop()) {
          mediaCol.style.removeProperty('position');
          mediaCol.style.removeProperty('top');
          return;
        }

        const offset = this._getHeaderBottom();
        const gap = 16;
        const topValue = (offset > 0 ? offset : 110) + gap;

        mediaCol.style.position = 'sticky';
        mediaCol.style.top = topValue + 'px';
      };

      requestAnimationFrame(apply);
      setTimeout(apply, 300);
      setTimeout(apply, 800);

      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => setTimeout(apply, 200), { once: true });
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => setTimeout(apply, 100));
      }

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(apply, 200);
      }, { passive: true });

      const announcementBar =
        document.querySelector('#shopify-section-announcement-bar') ||
        document.querySelector('.announcement-bar-section');

      if (announcementBar) {
        const obs = new MutationObserver(() => setTimeout(apply, 150));
        obs.observe(announcementBar, {
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden'],
        });
      }
    }

    _getHeaderBottom() {
      let maxBottom = 0;

      const selectors = [
        '#shopify-section-header',
        '#shopify-section-announcement-bar',
        '.shopify-section-header',
        '.shopify-section-announcement-bar',
        'header',
        '.header-wrapper',
        '.announcement-bar',
        'sticky-header',
      ];

      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          const style = window.getComputedStyle(el);
          const pos = style.position;
          if (pos !== 'sticky' && pos !== 'fixed') return;
          if (style.display === 'none' || style.visibility === 'hidden') return;

          const rect = el.getBoundingClientRect();
          if (rect.height <= 0 || rect.top > 100) return;

          const bottom = rect.top + rect.height;
          if (bottom > maxBottom) maxBottom = bottom;
        });
      });

      return maxBottom > 0 ? Math.ceil(maxBottom) : 0;
    }

    /* ===========================================================
       SLIDER ARROWS — Inject + bind
       =========================================================== */

    _injectSliderArrows() {
      if (this.totalMedia <= 1) return;

      const mainMedia = this.section.querySelector('.hulasi-product__main-media');
      if (!mainMedia || mainMedia.querySelector('.hulasi-product__slider-arrows')) return;

      const arrowsHTML = `
        <div class="hulasi-product__slider-arrows">
          <button type="button"
                  class="hulasi-product__slider-arrow hulasi-product__slider-arrow--prev"
                  aria-label="Previous image"
                  title="Previous image">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <button type="button"
                  class="hulasi-product__slider-arrow hulasi-product__slider-arrow--next"
                  aria-label="Next image"
                  title="Next image">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>`;

      mainMedia.insertAdjacentHTML('beforeend', arrowsHTML);

      this.arrowPrev = mainMedia.querySelector('.hulasi-product__slider-arrow--prev');
      this.arrowNext = mainMedia.querySelector('.hulasi-product__slider-arrow--next');

      this.arrowPrev.addEventListener('click', () => {
        if (this.currentIndex > 0) this.goToSlide(this.currentIndex - 1);
      });

      this.arrowNext.addEventListener('click', () => {
        if (this.currentIndex < this.slides.length - 1) this.goToSlide(this.currentIndex + 1);
      });

      this._updateArrowVisibility();
    }

    _updateArrowVisibility() {
      if (!this.arrowPrev || !this.arrowNext) return;
      this.arrowPrev.style.display = this.currentIndex === 0 ? 'none' : '';
      this.arrowNext.style.display = this.currentIndex === this.slides.length - 1 ? 'none' : '';
    }

    /* ===========================================================
       DYNAMIC THUMBNAIL FIT
       =========================================================== */

    _dynamicThumbnailFit() {
      if (window.matchMedia('(max-width: 989px)').matches) return;

      const thumbContainer = this.section.querySelector('.hulasi-product__thumbnails');
      if (!thumbContainer) return;

      const allThumbs = Array.from(thumbContainer.querySelectorAll('.hulasi-product__thumbnail'));
      if (allThumbs.length === 0) return;

      const doFit = () => {
        if (window.matchMedia('(max-width: 989px)').matches) return;

        allThumbs.forEach((t) => {
          t.style.display = '';
          t.removeAttribute('data-thumb-hidden');
          t.classList.remove('hulasi-product__thumbnail--more');
          const overlay = t.querySelector('.hulasi-product__thumbnail-more-overlay');
          if (overlay) overlay.remove();
        });

        const imageCard = this.section.querySelector('.hulasi-product__image-card');
        if (!imageCard) return;
        const availableHeight = imageCard.offsetHeight;
        if (availableHeight <= 0) return;

        const thumbHeight = 68;
        const gap = 10;
        const maxFit = Math.floor((availableHeight + gap) / (thumbHeight + gap));

        if (maxFit >= allThumbs.length) {
          return;
        }

        const showCount = Math.max(1, maxFit - 1);
        const hiddenCount = allThumbs.length - showCount;

        allThumbs.forEach((thumb, i) => {
          if (i < showCount) {
            thumb.style.display = '';
            thumb.removeAttribute('data-thumb-hidden');
          } else if (i === showCount) {
            thumb.style.display = '';
            thumb.removeAttribute('data-thumb-hidden');
            thumb.classList.add('hulasi-product__thumbnail--more');
            thumb.dataset.action = 'open-zoom-all';

            if (!thumb.querySelector('.hulasi-product__thumbnail-more-overlay')) {
              const overlay = document.createElement('span');
              overlay.className = 'hulasi-product__thumbnail-more-overlay';
              overlay.setAttribute('aria-hidden', 'true');
              overlay.textContent = '+' + hiddenCount;
              thumb.appendChild(overlay);
            }
          } else {
            thumb.style.display = 'none';
            thumb.setAttribute('data-thumb-hidden', 'true');
          }
        });
      };

      const imageCard = this.section.querySelector('.hulasi-product__image-card');
      if (imageCard) {
        const img = imageCard.querySelector('.hulasi-product__media-image');
        if (img && img.complete) {
          requestAnimationFrame(doFit);
        } else if (img) {
          img.addEventListener('load', () => requestAnimationFrame(doFit), { once: true });
        }
      }

      setTimeout(doFit, 500);
      setTimeout(doFit, 1200);

      let fitTimer;
      window.addEventListener('resize', () => {
        clearTimeout(fitTimer);
        fitTimer = setTimeout(doFit, 250);
      }, { passive: true });

      this._doThumbFit = doFit;
    }

    /* ===========================================================
       GALLERY
       =========================================================== */

    _collectImageSlides() {
      this.zoomImages = [];
      this.imageSlideIndices = [];
      this.slides.forEach((slide, i) => {
        if (slide.dataset.mediaType === 'image') {
          const wrap = slide.querySelector('.hulasi-product__media-image-wrap');
          if (wrap) {
            this.zoomImages.push({
              url: wrap.dataset.zoomUrl || '',
              alt: wrap.dataset.zoomAlt || '',
              slideIndex: i,
            });
            this.imageSlideIndices.push(i);
          }
        }
      });
    }

    _bindThumbnails() {
      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (thumb.dataset.action === 'open-zoom-all') {
            this._openZoom(0);
            return;
          }
          this.goToSlide(parseInt(thumb.dataset.mediaIndex, 10));
        });
      });
    }

    goToSlide(index) {
      if (index === this.currentIndex || index < 0 || index >= this.slides.length) return;
      const cur = this.currentIndex;

      if (this.slides[cur]) {
        this.slides[cur].setAttribute('hidden', '');
        this.slides[cur].classList.remove('hulasi-product__media-slide--active');
      }
      if (this.dots[cur]) this.dots[cur].classList.remove('hulasi-product__media-dot--active');

      this.thumbnails.forEach((t) => {
        if (t.dataset.action !== 'open-zoom-all') {
          const tIdx = parseInt(t.dataset.mediaIndex, 10);
          if (tIdx === cur) {
            t.classList.remove('hulasi-product__thumbnail--active');
            t.setAttribute('aria-current', 'false');
          }
          if (tIdx === index) {
            t.classList.add('hulasi-product__thumbnail--active');
            t.setAttribute('aria-current', 'true');
            t.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }
      });

      if (this.slides[index]) {
        this.slides[index].removeAttribute('hidden');
        this.slides[index].classList.add('hulasi-product__media-slide--active');
      }
      if (this.dots[index]) this.dots[index].classList.add('hulasi-product__media-dot--active');

      this._pauseMediaOnSlide(cur);
      this.currentIndex = index;

      this._updateArrowVisibility();
    }

    _pauseMediaOnSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;
      const video = slide.querySelector('video');
      if (video) video.pause();
      const iframe = slide.querySelector('iframe');
      if (iframe && iframe.contentWindow) {
        try {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        } catch (e) { /* ignore */ }
      }
    }

    /* ===========================================================
       MOBILE SWIPE
       =========================================================== */

    _bindMobileSwipe() {
      const mainMedia = this.section.querySelector('.hulasi-product__main-media');
      if (!mainMedia) return;
      let startX = 0, startY = 0;
      const THRESHOLD = 50;

      mainMedia.addEventListener('touchstart', (e) => {
        if (e.target.closest('.hulasi-product__media-video-wrap')) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      mainMedia.addEventListener('touchend', (e) => {
        if (e.target.closest('.hulasi-product__media-video-wrap')) return;
        const diffX = e.changedTouches[0].clientX - startX;
        const diffY = Math.abs(e.changedTouches[0].clientY - startY);
        if (Math.abs(diffX) > THRESHOLD && diffY < Math.abs(diffX)) {
          if (diffX < 0 && this.currentIndex < this.slides.length - 1) this.goToSlide(this.currentIndex + 1);
          else if (diffX > 0 && this.currentIndex > 0) this.goToSlide(this.currentIndex - 1);
        }
      }, { passive: true });
    }

    /* ===========================================================
       ZOOM MODAL
       =========================================================== */

    _bindZoomBtn() {
      if (!this.zoomBtn) return;
      this.zoomBtn.addEventListener('click', () => {
        const zoomIdx = this.zoomImages.findIndex(
          (img) => img.slideIndex === this.currentIndex
        );
        this._openZoom(zoomIdx !== -1 ? zoomIdx : 0);
      });
    }

    _bindZoomModalControls() {
      if (!this.zoomModal) return;

      const closeBtn = this.zoomModal.querySelector('.hulasi-zoom-modal__close');
      if (closeBtn) {
        this.zoomClose = closeBtn;
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._closeZoom();
        });
        closeBtn.addEventListener('touchend', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._closeZoom();
        }, { passive: false });
      }

      const backdrop = this.zoomModal.querySelector('.hulasi-zoom-modal__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', () => this._closeZoom());
        backdrop.addEventListener('touchend', (e) => {
          e.preventDefault();
          this._closeZoom();
        }, { passive: false });
      }

      if (this.zoomPrev) this.zoomPrev.addEventListener('click', () => this._zoomNavigate(-1));
      if (this.zoomNext) this.zoomNext.addEventListener('click', () => this._zoomNavigate(1));
    }

    _openZoom(index) {
      if (!this.zoomModal || this.zoomImages.length === 0) return;
      this.zoomCurrentIndex = Math.max(0, Math.min(index, this.zoomImages.length - 1));
      this._loadZoomImage(this.zoomCurrentIndex);
      this.zoomModal.removeAttribute('hidden');
      void this.zoomModal.offsetHeight;
      this.zoomModal.classList.add('hulasi-zoom--open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeyDown);

      requestAnimationFrame(() => {
        const closeBtn = this.zoomModal.querySelector('.hulasi-zoom-modal__close');
        if (closeBtn) {
          closeBtn.style.display = 'flex';
          closeBtn.focus();
        }
      });
    }

    _closeZoom() {
      if (!this.zoomModal) return;
      this.zoomModal.classList.remove('hulasi-zoom--open');
      setTimeout(() => {
        this.zoomModal.setAttribute('hidden', '');
        this._resetZoomTransform();
      }, 300);
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeyDown);
    }

    _zoomNavigate(dir) {
      const n = this.zoomCurrentIndex + dir;
      if (n < 0 || n >= this.zoomImages.length) return;
      this.zoomCurrentIndex = n;
      this._resetZoomTransform();
      this._loadZoomImage(n);
    }

    _loadZoomImage(index) {
      const img = this.zoomImages[index];
      if (!img || !this.zoomImage) return;
      this.zoomImage.src = img.url;
      this.zoomImage.alt = img.alt;
      if (this.zoomCounter) this.zoomCounter.textContent = `${index + 1} / ${this.zoomImages.length}`;
      if (this.zoomPrev) this.zoomPrev.disabled = index === 0;
      if (this.zoomNext) this.zoomNext.disabled = index === this.zoomImages.length - 1;
    }

    _handleKeyDown(e) {
      if (e.key === 'Escape') this._closeZoom();
      else if (e.key === 'ArrowLeft') this._zoomNavigate(-1);
      else if (e.key === 'ArrowRight') this._zoomNavigate(1);
    }

    /* ===========================================================
       PINCH ZOOM + PAN
       =========================================================== */

    _bindPinchZoom() {
      const container = this.zoomContainer;
      const img = this.zoomImage;
      if (!container || !img) return;
      const state = this._zoomState;
      const pinch = this._pinchState;
      const pan = this._panState;

      container.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          pinch.startDist = this._getTouchDistance(e.touches);
          pinch.startScale = state.scale;
        } else if (e.touches.length === 1 && state.scale > 1) {
          e.preventDefault();
          pan.active = true;
          pan.startX = e.touches[0].clientX - state.x;
          pan.startY = e.touches[0].clientY - state.y;
        }
      }, { passive: false });

      container.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          const dist = this._getTouchDistance(e.touches);
          state.scale = Math.min(Math.max(pinch.startScale * (dist / pinch.startDist), 1), 5);
          if (state.scale <= 1) { state.x = 0; state.y = 0; }
          this._applyZoomTransform();
        } else if (e.touches.length === 1 && pan.active && state.scale > 1) {
          e.preventDefault();
          state.x = e.touches[0].clientX - pan.startX;
          state.y = e.touches[0].clientY - pan.startY;
          this._clampPan();
          this._applyZoomTransform();
        }
      }, { passive: false });

      container.addEventListener('touchend', (e) => {
        if (e.touches.length < 2) pinch.startDist = null;
        if (e.touches.length === 0) {
          pan.active = false;
          if (state.scale <= 1.05) this._resetZoomTransform();
        }
      }, { passive: true });

      let lastTap = 0;
      container.addEventListener('touchend', (e) => {
        if (e.touches.length > 0) return;
        const now = Date.now();
        if (now - lastTap < 300) {
          e.preventDefault();
          if (state.scale > 1) {
            this._resetZoomTransform();
          } else {
            state.scale = 2.5;
            const rect = container.getBoundingClientRect();
            const tapX = e.changedTouches[0].clientX - rect.left;
            const tapY = e.changedTouches[0].clientY - rect.top;
            state.x = (rect.width / 2 - tapX) * (state.scale - 1);
            state.y = (rect.height / 2 - tapY) * (state.scale - 1);
            this._clampPan();
            this._applyZoomTransform();
          }
        }
        lastTap = now;
      }, { passive: false });

      let mouseDown = false, mStartX = 0, mStartY = 0;
      container.addEventListener('mousedown', (e) => {
        if (state.scale <= 1) return;
        e.preventDefault();
        mouseDown = true;
        mStartX = e.clientX - state.x;
        mStartY = e.clientY - state.y;
        container.style.cursor = 'grabbing';
      });
      container.addEventListener('mousemove', (e) => {
        if (!mouseDown || state.scale <= 1) return;
        state.x = e.clientX - mStartX;
        state.y = e.clientY - mStartY;
        this._clampPan();
        this._applyZoomTransform();
      });
      container.addEventListener('mouseup', () => {
        mouseDown = false;
        container.style.cursor = state.scale > 1 ? 'grab' : '';
      });
      container.addEventListener('mouseleave', () => {
        mouseDown = false;
        container.style.cursor = state.scale > 1 ? 'grab' : '';
      });

      container.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.3 : 0.3;
        const newScale = Math.min(Math.max(state.scale + delta, 1), 5);
        if (newScale <= 1) this._resetZoomTransform();
        else {
          state.scale = newScale;
          this._clampPan();
          this._applyZoomTransform();
        }
      }, { passive: false });
    }

    _getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    _applyZoomTransform() {
      if (!this.zoomImage) return;
      const { scale, x, y } = this._zoomState;
      this.zoomImage.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
      this.zoomImage.style.transformOrigin = 'center center';
      if (this.zoomContainer) this.zoomContainer.style.cursor = scale > 1 ? 'grab' : '';
    }

    _clampPan() {
      if (!this.zoomContainer || !this.zoomImage) return;
      const { scale } = this._zoomState;
      const cRect = this.zoomContainer.getBoundingClientRect();
      const nW = this.zoomImage.naturalWidth || cRect.width;
      const nH = this.zoomImage.naturalHeight || cRect.height;
      const aspect = nW / nH;
      let rW, rH;
      if (cRect.width / cRect.height > aspect) { rH = cRect.height; rW = rH * aspect; }
      else { rW = cRect.width; rH = rW / aspect; }
      const maxX = Math.max(0, (rW * scale - cRect.width) / 2);
      const maxY = Math.max(0, (rH * scale - cRect.height) / 2);
      this._zoomState.x = Math.min(maxX, Math.max(-maxX, this._zoomState.x));
      this._zoomState.y = Math.min(maxY, Math.max(-maxY, this._zoomState.y));
    }

    _resetZoomTransform() {
      this._zoomState.scale = 1;
      this._zoomState.x = 0;
      this._zoomState.y = 0;
      if (this.zoomImage) this.zoomImage.style.transform = '';
      if (this.zoomContainer) this.zoomContainer.style.cursor = '';
    }

    /* ===========================================================
       RATING PILL
       =========================================================== */

    _initRatingPill() {
      if (!this.ratingPill) return;
      const handle = this.productHandle;
      const id = this.productId;
      if (!handle && !id) return;

      const hashStr = (handle || '') + '-' + (id || '');
      let h = 0;
      for (let i = 0; i < hashStr.length; i++) {
        h = ((h << 5) - h) + hashStr.charCodeAt(i);
        h |= 0;
      }
      h = Math.abs(h);

      const rating = (4.0 + (h % 11) / 10).toFixed(1);
      const count = 8 + (h % 143);

      const scoreEl = this.section.querySelector(`#HulasiRatingScore-${this.sectionId}`);
      const countEl = this.section.querySelector(`#HulasiRatingCount-${this.sectionId}`);

      if (scoreEl) scoreEl.textContent = rating;
      if (countEl) countEl.textContent = '(' + count + ')';
    }

    /* ===========================================================
       SIMILAR SCROLL
       =========================================================== */

    _bindSimilarScroll() {
      if (!this.similarBtn) return;
      this.similarBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.getElementById('HulasiSimilarProducts');
        if (!target) return;

        const header = document.querySelector('header, .header-wrapper, .section-header, sticky-header');
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        const offset = headerHeight + 20;

        const targetPos = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: targetPos, behavior: 'smooth' });
      });
    }

    /* ===========================================================
       SHARE BUTTON
       =========================================================== */

    _initShareButton() {
      if (!this.shareBtn) return;

      const title = this.shareBtn.dataset.productTitle || document.title;
      const url = this.shareBtn.dataset.productUrl || window.location.href;

      this.shareBtn.addEventListener('click', async () => {
        if (navigator.share) {
          try {
            await navigator.share({ title: title, url: url });
            return;
          } catch (err) {
            if (err.name === 'AbortError') return;
          }
        }
        this._openShareModal(title, url);
      });
    }

    _openShareModal(title, url) {
      if (!this.shareModal) return;
      const encodedUrl = encodeURIComponent(url);
      const encodedTitle = encodeURIComponent(title);

      const waLink = this.shareModal.querySelector(`#HulasiShareWhatsApp-${this.sectionId}`);
      const fbLink = this.shareModal.querySelector(`#HulasiShareFacebook-${this.sectionId}`);
      const twLink = this.shareModal.querySelector(`#HulasiShareTwitter-${this.sectionId}`);
      const copyBtn = this.shareModal.querySelector(`#HulasiShareCopy-${this.sectionId}`);

      if (waLink) waLink.href = `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`;
      if (fbLink) fbLink.href = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      if (twLink) twLink.href = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;

      if (copyBtn) {
        const newCopyBtn = copyBtn.cloneNode(true);
        copyBtn.parentNode.replaceChild(newCopyBtn, copyBtn);
        newCopyBtn.addEventListener('click', () => {
          navigator.clipboard.writeText(url).then(() => {
            newCopyBtn.classList.add('is-copied');
            const span = newCopyBtn.querySelector('span');
            if (span) span.textContent = 'Copied!';
            setTimeout(() => {
              newCopyBtn.classList.remove('is-copied');
              if (span) span.textContent = 'Copy link';
            }, 2000);
          }).catch(() => {
            const ta = document.createElement('textarea');
            ta.value = url;
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            const span = newCopyBtn.querySelector('span');
            if (span) span.textContent = 'Copied!';
            setTimeout(() => { if (span) span.textContent = 'Copy link'; }, 2000);
          });
        });
      }

      this.shareModal.removeAttribute('hidden');
      void this.shareModal.offsetHeight;
      this.shareModal.classList.add('hulasi-share--open');
      document.body.style.overflow = 'hidden';

      const closeModal = () => {
        this.shareModal.classList.remove('hulasi-share--open');
        setTimeout(() => {
          this.shareModal.setAttribute('hidden', '');
        }, 250);
        document.body.style.overflow = '';
      };

      const closeBtn = this.shareModal.querySelector(`#HulasiShareModalClose-${this.sectionId}`);
      const backdrop = this.shareModal.querySelector('.hulasi-share-modal__backdrop');

      if (closeBtn) {
        const newClose = closeBtn.cloneNode(true);
        closeBtn.parentNode.replaceChild(newClose, closeBtn);
        newClose.addEventListener('click', closeModal);
      }
      if (backdrop) {
        const newBackdrop = backdrop.cloneNode(true);
        backdrop.parentNode.replaceChild(newBackdrop, backdrop);
        newBackdrop.addEventListener('click', closeModal);
      }

      const escHandler = (e) => {
        if (e.key === 'Escape') {
          closeModal();
          document.removeEventListener('keydown', escHandler);
        }
      };
      document.addEventListener('keydown', escHandler);
    }

    /* ===========================================================
       WISHLIST
       =========================================================== */

    _initWishlistButton() {
      if (!this.wishlistBtn) return;

      const productId = this.wishlistBtn.dataset.productId;
      const variantId = this.wishlistBtn.dataset.variantId;
      const productUrl = this.wishlistBtn.dataset.productUrl || window.location.href;

      this._whenSwymReady((swat) => {
        this._checkWishlistState(swat, productId, variantId);
      });

      this.wishlistBtn.addEventListener('click', () => {
        this._whenSwymReady((swat) => {
          this._toggleWishlist(swat, productId, variantId, productUrl);
        });
      });
    }

    _whenSwymReady(callback) {
      if (window._swat) {
        callback(window._swat);
        return;
      }
      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push(callback);
    }

    _checkWishlistState(swat, productId, variantId) {
      if (!swat || typeof swat.fetch !== 'function') return;
      try {
        swat.fetch((items) => {
          const isWishlisted = items.some(
            (item) => String(item.epi) === String(variantId) || String(item.empi) === String(productId)
          );
          this._updateWishlistUI(isWishlisted);
        });
      } catch (e) { /* ignore */ }
    }

    _toggleWishlist(swat, productId, variantId, productUrl) {
      if (!swat) return;
      const isCurrentlyWishlisted = this.wishlistBtn.classList.contains('is-wishlisted');

      const product = {
        epi: parseInt(variantId, 10),
        empi: parseInt(productId, 10),
        du: productUrl,
      };

      if (isCurrentlyWishlisted) {
        swat.removeFromWishList(product, () => {
          this._updateWishlistUI(false);
        }, (err) => {
          console.warn('[Hulasi Wishlist] Remove error:', err);
        });
      } else {
        swat.addToWishList(product, () => {
          this._updateWishlistUI(true);
        }, (err) => {
          console.warn('[Hulasi Wishlist] Add error:', err);
        });
      }
    }

    _updateWishlistUI(isWishlisted) {
      if (!this.wishlistBtn) return;
      const emptyIcon = this.wishlistBtn.querySelector('.hulasi-product__wishlist-icon--empty');
      const filledIcon = this.wishlistBtn.querySelector('.hulasi-product__wishlist-icon--filled');

      if (isWishlisted) {
        this.wishlistBtn.classList.add('is-wishlisted');
        this.wishlistBtn.setAttribute('aria-label', 'Remove from wishlist');
        if (emptyIcon) emptyIcon.setAttribute('hidden', '');
        if (filledIcon) filledIcon.removeAttribute('hidden');
      } else {
        this.wishlistBtn.classList.remove('is-wishlisted');
        this.wishlistBtn.setAttribute('aria-label', 'Add to wishlist');
        if (emptyIcon) emptyIcon.removeAttribute('hidden');
        if (filledIcon) filledIcon.setAttribute('hidden', '');
      }
    }

    /* ===========================================================
       PERSONALIZATION
       =========================================================== */

    _initPersonalization() {
      if (!this.personalizationContainer || !this.nameInput) return;
      const c = this.personalizationContainer;
      this._minLen = parseInt(c.dataset.minLength, 10) || 1;
      this._maxLen = parseInt(c.dataset.maxLength, 10) || 12;
      this._placeholder = c.dataset.placeholder || 'Your Name';
      this._currentFont = c.dataset.defaultFont || 'Great Vibes';
      this._enableHindi = c.dataset.enableHindi === 'true';
      this._errorMessages = {
        empty: c.dataset.errorEmpty || 'Please enter a name for personalisation',
        invalid: c.dataset.errorInvalid || 'Only letters and spaces are allowed',
        min: c.dataset.errorMin || 'Name must be at least 1 character(s)',
      };

      this._updatePreviewFont(this._currentFont);

      this.nameInput.addEventListener('input', () => this._onNameInput());
      this.nameInput.addEventListener('blur', () => this._validateName(true));

      this.fontRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this._currentFont = radio.value;
          this._updatePreviewFont(radio.value);
          this._updateFontOptionStyles();
          this._updatePreview();
        });
      });

      this.langRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this._currentLang = radio.value;
          this._updateLangToggleStyles();
          this._updatePreview();
          if (this.nameInput) {
            this.nameInput.placeholder = this._currentLang === 'hi' ? 'Apna naam likhen (Hinglish mein)' : this._placeholder;
          }
        });
      });

      this._checkTransliterateReady();
    }

    _checkTransliterateReady() {
      if (!this._enableHindi) return;
      if (window.HulasiTransliterate) { this._transliterateReady = true; return; }
      let attempts = 0;
      const check = setInterval(() => {
        attempts++;
        if (window.HulasiTransliterate) {
          this._transliterateReady = true;
          clearInterval(check);
          if (this._currentLang === 'hi') this._updatePreview();
        }
        if (attempts > 50) clearInterval(check);
      }, 100);
    }

    _onNameInput() {
      this._rawInput = this.nameInput.value;
      if (this.charCount) this.charCount.textContent = `${this._rawInput.length}/${this._maxLen}`;
      this._clearError();
      this._updatePreview();
    }

    _updatePreview() {
      if (!this.previewText) return;
      let displayText = this._rawInput.trim();

      if (this._currentLang === 'hi' && displayText.length > 0 && this._transliterateReady && window.HulasiTransliterate) {
        try { displayText = window.HulasiTransliterate.toHindi(displayText); } catch (e) { /* ignore */ }
      }

      if (displayText.length === 0) {
        let ph = this._placeholder;
        if (this._currentLang === 'hi' && this._transliterateReady && window.HulasiTransliterate) {
          try { ph = window.HulasiTransliterate.toHindi(ph); } catch (e) { /* ignore */ }
        }
        this.previewText.textContent = ph;
        this.previewText.classList.add('hulasi-product__personalization-preview-text--placeholder');
      } else {
        this.previewText.textContent = displayText;
        this.previewText.classList.remove('hulasi-product__personalization-preview-text--placeholder');
      }
      this._syncHiddenInput();
    }

    _syncHiddenInput() {
      if (!this.hiddenNameInput) return;
      let finalText = this._rawInput.trim();
      if (this._currentLang === 'hi' && finalText.length > 0 && this._transliterateReady && window.HulasiTransliterate) {
        try { finalText = window.HulasiTransliterate.toHindi(finalText); } catch (e) { /* ignore */ }
      }
      this.hiddenNameInput.value = finalText;
    }

    _updatePreviewFont(fontName) {
      if (this.previewText) this.previewText.style.fontFamily = `'${fontName}', cursive`;
    }

    _updateFontOptionStyles() {
      this.fontOptions.forEach((opt) => {
        const r = opt.querySelector('.hulasi-personalization__font-radio');
        opt.classList.toggle('hulasi-personalization__font-option--active', r && r.checked);
      });
    }

    _updateLangToggleStyles() {
      this.langBtns.forEach((btn) => {
        const r = btn.querySelector('.hulasi-personalization__lang-radio');
        btn.classList.toggle('hulasi-personalization__toggle-btn--active', r && r.checked);
      });
    }

    _validateName(showError = true) {
      if (!this.isPersonalized || !this.nameInput) return true;
      const val = this._rawInput.trim();
      const latinRegex = /^[a-zA-Z\s]+$/;
      let errorMsg = '';

      if (val.length === 0) errorMsg = this._errorMessages.empty;
      else if (val.length < this._minLen) errorMsg = this._errorMessages.min.replace('{min}', String(this._minLen));
      else if (!latinRegex.test(val)) errorMsg = this._errorMessages.invalid;

      if (errorMsg) { if (showError) this._showError(errorMsg); return false; }
      this._clearError();
      this.nameInput.classList.remove('hulasi-personalization__input--error');
      this.nameInput.classList.add('hulasi-personalization__input--valid');
      return true;
    }

    _showError(msg) {
      if (!this.errorEl || !this.nameInput) return;
      this.errorEl.textContent = msg;
      this.errorEl.removeAttribute('hidden');
      this.nameInput.classList.add('hulasi-personalization__input--error');
      this.nameInput.classList.remove('hulasi-personalization__input--valid');
      this.nameInput.classList.remove('hulasi-personalization__input--shake');
      void this.nameInput.offsetHeight;
      this.nameInput.classList.add('hulasi-personalization__input--shake');
    }

    _clearError() {
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.setAttribute('hidden', '');
      }
      if (this.nameInput) this.nameInput.classList.remove('hulasi-personalization__input--error');
    }

    _scrollToPersonalization() {
      if (!this.personalizationContainer) return;
      this.personalizationContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => { if (this.nameInput) this.nameInput.focus(); }, 400);
    }

    /* ===========================================================
       VARIANT CHANGE
       =========================================================== */

    _parseVariantsJson() {
      const script = this.section.querySelector('script[type="application/json"]');
      if (!script) return [];
      try { return JSON.parse(script.textContent); } catch (e) { return []; }
    }

    _bindVariantChange() {
      if (this.variantSelects.length === 0 || this.variantsData.length === 0) return;
      this.variantSelects.forEach((select) => {
        select.addEventListener('change', () => this._onVariantChange());
      });
    }

    _onVariantChange() {
      const selectedOptions = [];
      this.variantSelects.forEach((s) => selectedOptions.push(s.value));
      const matched = this.variantsData.find((v) => v.options.every((o, i) => o === selectedOptions[i]));
      if (!matched) return;

      if (this.variantIdInput) this.variantIdInput.value = matched.id;

      if (this.wishlistBtn) {
        this.wishlistBtn.dataset.variantId = matched.id;
        this._whenSwymReady((swat) => {
          this._checkWishlistState(swat, this.productId, matched.id);
        });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', matched.id);
      window.history.replaceState({}, '', url.toString());

      this._updatePriceDisplay(matched);
      this._updateButtonStates(matched);
    }

    _formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
      const amount = (cents / 100).toFixed(2);
      return '\u20B9' + amount.replace(/\d(?=(\d{3})+\.)/g, '$&,').replace('.00', '');
    }

    _updatePriceDisplay(variant) {
      const pw = this.section.querySelector(`#HulasiPrice-${this.sectionId}`);
      if (!pw) return;
      let html = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += `<span class="hulasi-product__price-compare visually-hidden">Regular price</span><s class="hulasi-product__price-compare-value">${this._formatMoney(variant.compare_at_price)}</s>`;
      }
      html += `<span class="hulasi-product__price-current">${this._formatMoney(variant.price)}</span>`;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const save = variant.compare_at_price - variant.price;
        const pct = Math.round((save / variant.compare_at_price) * 100);
        html += `<span class="hulasi-product__price-save"><span class="hulasi-product__price-save-icon" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.75v10.5M3.5 5.25L7 1.75l3.5 3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg></span>You save ${this._formatMoney(save)}<span class="hulasi-product__price-save-percent">(${pct}% off)</span></span>`;
      }
      pw.innerHTML = html;
      this._updateStickySave(variant);
    }

    _updateStickySave(variant) {
      const el = this.section.querySelector(`#HulasiStickySave-${this.sectionId}`);
      if (!el) return;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const save = variant.compare_at_price - variant.price;
        const pct = Math.round((save / variant.compare_at_price) * 100);
        el.innerHTML = `<span class="hulasi-product__sticky-save-icon" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 1.75v10.5M3.5 5.25L7 1.75l3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>Save ${this._formatMoney(save)} (${pct}%)`;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    }

    _updateButtonStates(variant) {
      const atcBtn = this.section.querySelector(`#HulasiBtnATC-${this.sectionId}`);
      const atcText = this.section.querySelector(`#HulasiBtnATCText-${this.sectionId}`);
      const buyBtn = this.section.querySelector(`#HulasiBtnBuyNow-${this.sectionId}`);

      if (variant.available) {
        if (atcBtn) atcBtn.disabled = false;
        if (atcText) atcText.textContent = 'Add to Cart';
        if (buyBtn) { buyBtn.disabled = false; buyBtn.style.display = ''; }
        if (this.stickyATC) this.stickyATC.disabled = false;
        if (this.stickyBuyNow) { this.stickyBuyNow.disabled = false; this.stickyBuyNow.style.display = ''; }
      } else {
        if (atcBtn) atcBtn.disabled = true;
        if (atcText) atcText.textContent = 'Sold Out';
        if (buyBtn) { buyBtn.disabled = true; buyBtn.style.display = 'none'; }
        if (this.stickyATC) this.stickyATC.disabled = true;
        if (this.stickyBuyNow) { this.stickyBuyNow.disabled = true; this.stickyBuyNow.style.display = 'none'; }
      }

      if (variant.featured_media) {
        const target = this.section.querySelector(`.hulasi-product__media-slide[data-media-id="${variant.featured_media.id}"]`);
        if (target) this.goToSlide(parseInt(target.dataset.mediaIndex, 10));
      }
    }

    /* ===========================================================
       FORM SUBMIT
       =========================================================== */

    _bindFormSubmit() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        const isBuyNow = e.submitter && e.submitter.name === 'checkout';
        if (this.isPersonalized && !this._validateName(true)) {
          e.preventDefault();
          this._scrollToPersonalization();
          return;
        }
        if (this.isPersonalized) this._syncHiddenInput();
        if (this.returnToInput) {
          if (isBuyNow) this.returnToInput.disabled = true;
          else {
            this.returnToInput.disabled = false;
            this.returnToInput.value = '/cart';
          }
        }
      });
    }

    _bindStickyBarButtons() {
      if (this.stickyATC) this.stickyATC.addEventListener('click', () => this._handleStickySubmit('add'));
      if (this.stickyBuyNow) this.stickyBuyNow.addEventListener('click', () => this._handleStickySubmit('checkout'));
    }

    _handleStickySubmit(action) {
      if (this.isPersonalized && !this._validateName(true)) {
        this._scrollToPersonalization();
        return;
      }
      if (this.isPersonalized) this._syncHiddenInput();
      if (!this.form) return;

      if (action === 'checkout') {
        if (this.returnToInput) this.returnToInput.disabled = true;
      } else {
        if (this.returnToInput) {
          this.returnToInput.disabled = false;
          this.returnToInput.value = '/cart';
        }
      }

      const tempBtn = document.createElement('button');
      tempBtn.type = 'submit';
      tempBtn.name = action === 'checkout' ? 'checkout' : 'add';
      tempBtn.style.display = 'none';
      this.form.appendChild(tempBtn);
      tempBtn.click();
      tempBtn.remove();
    }

    /* ===========================================================
       STICKY BAR OBSERVER
       =========================================================== */

    _initStickyBarObserver() {
      if (!this.stickyBar || !this.buyButtonsBlock) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.stickyBar.setAttribute('aria-hidden', 'true');
          } else {
            this.stickyBar.setAttribute('aria-hidden', 'false');
          }
        });
      }, { threshold: 0 });
      observer.observe(this.buyButtonsBlock);
    }

    /* ===========================================================
       PINCODE CHECKER
       =========================================================== */

    _initPincodeChecker() {
      if (!this.pincodeContainer || !this.pincodeInput || !this.pincodeBtn) return;
      this._pincodeCacheKey = this.pincodeContainer.dataset.cacheKey || 'hulasi-pincode-v2';

      this.pincodeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6);
        e.target.classList.remove('hulasi-pincode__input--error');
        if (this.pincodeResult) {
          this.pincodeResult.innerHTML = '';
          this.pincodeResult.setAttribute('hidden', '');
        }
      });
      this.pincodeInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this._checkPincode(); }
      });
      this.pincodeBtn.addEventListener('click', () => this._checkPincode());

      const saved = this._getSavedPincode();
      if (saved) {
        this.pincodeInput.value = saved;
        setTimeout(() => this._checkPincode(true), 300);
      }
    }

    async _checkPincode(silent = false) {
      const pincode = this.pincodeInput.value.trim();
      if (!/^[1-9]\d{5}$/.test(pincode)) {
        if (!silent) this._showPincodeError('Please enter a valid 6-digit Indian pincode');
        this.pincodeInput.classList.add('hulasi-pincode__input--error');
        return;
      }
      this.pincodeInput.classList.remove('hulasi-pincode__input--error');
      this._setPincodeLoading(true);

      try {
        if (!this.pincodeContainer.dataset.csvUrl) {
          const exists = await this._verifyPostalPincode(pincode);
          if (this.pincodeInput.value.trim() !== pincode) return;
          if (!exists) {
            this._showPincodeError('This Indian pincode could not be found. Please check and try again.');
            return;
          }
          this._showPincodeTypicalEstimate(pincode);
          this._savePincode(pincode);
          return;
        }
        await this._ensurePincodeData();
        if (this.pincodeInput.value.trim() !== pincode) return;
        const zone = this._lookupZone(pincode);
        if (!zone) {
          this._showPincodeError("We can't confirm delivery to this pincode yet. Please contact us for an estimate.");
          return;
        }
        const result = this._calculateDelivery(zone, pincode);
        this._showPincodeSuccess(result, pincode);
        this._savePincode(pincode);
      } catch (err) {
        if (this.pincodeInput.value.trim() === pincode) {
          this._showPincodeError('Pincode check is temporarily unavailable. Please try again.');
        }
      } finally {
        this._setPincodeLoading(false);
      }
    }

    async _verifyPostalPincode(pincode) {
      if (this._verifiedPostalPincodes.has(pincode)) return true;
      const response = await fetch(`https://api.pincodeapi.in/api/v1/pincode/${pincode}`, {
        signal: AbortSignal.timeout(8000)
      });
      if (response.status === 404) return false;
      if (!response.ok) throw new Error(`Pincode lookup failed: ${response.status}`);
      const payload = await response.json();
      const exists = payload.success === true &&
        payload.data?.pincode === pincode &&
        Array.isArray(payload.data.post_offices) &&
        payload.data.post_offices.length > 0;
      if (exists) this._verifiedPostalPincodes.add(pincode);
      return exists;
    }

    async _ensurePincodeData() {
      if (this._pincodeData) return;
      const csvUrl = this.pincodeContainer.dataset.csvUrl;
      if (!csvUrl) throw new Error('Pincode data is not configured');
      const cached = this._getPincodeCache();
      if (cached && Object.keys(cached).length > 0) { this._pincodeData = cached; return; }
      const response = await fetch(csvUrl);
      if (!response.ok) throw new Error(`CSV fetch failed: ${response.status}`);
      const csvText = await response.text();
      const pincodeData = this._parseCSV(csvText);
      if (Object.keys(pincodeData).length === 0) throw new Error('Pincode data is empty');
      this._pincodeData = pincodeData;
      this._setPincodeCache(this._pincodeData);
    }

    _parseCSV(csvText) {
      const data = {};
      const lines = csvText.split(/\r?\n/);
      if (lines.length === 0) return data;
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
      let pincodeCol = headers.findIndex(h => ['pincode', 'pin', 'pin_code', 'postal_code', 'destination_pincode'].includes(h));
      let zoneCol = headers.findIndex(h => ['zone', 'delivery_zone', 'region'].includes(h));
      if (pincodeCol === -1) pincodeCol = 0;
      if (zoneCol === -1 && headers.length > 1) zoneCol = 1;

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const cols = line.split(',').map(c => c.trim().replace(/"/g, ''));
        const pin = cols[pincodeCol];
        const zone = zoneCol !== -1 ? cols[zoneCol] : '';
        if (pin && /^[1-9]\d{5}$/.test(pin)) data[pin] = this._normalizeZone(zone);
      }
      return data;
    }

    _normalizeZone(zone) {
      if (!zone) return 'default';
      const z = zone.trim().toUpperCase();
      if (['A', 'ZONE A', 'ZONE_A', 'METRO', 'LOCAL'].includes(z) || z.startsWith('A')) return 'a';
      if (['B', 'ZONE B', 'ZONE_B', 'REGIONAL', 'WITHIN STATE'].includes(z) || z.startsWith('B')) return 'b';
      if (['C', 'ZONE C', 'ZONE_C', 'REMOTE', 'REST OF INDIA', 'ROI', 'NATIONAL'].includes(z) || z.startsWith('C')) return 'c';
      return 'default';
    }

    _lookupZone(pincode) {
      if (!this._pincodeData || !Object.prototype.hasOwnProperty.call(this._pincodeData, pincode)) return null;
      return this._pincodeData[pincode];
    }

    _calculateDelivery(zone, pincode) {
      const c = this.pincodeContainer;
      let minD, maxD;
      switch (zone) {
        case 'a': minD = parseInt(c.dataset.zoneAMin, 10) || 2; maxD = parseInt(c.dataset.zoneAMax, 10) || 3; break;
        case 'b': minD = parseInt(c.dataset.zoneBMin, 10) || 4; maxD = parseInt(c.dataset.zoneBMax, 10) || 6; break;
        case 'c': minD = parseInt(c.dataset.zoneCMin, 10) || 6; maxD = parseInt(c.dataset.zoneCMax, 10) || 9; break;
        default: minD = parseInt(c.dataset.zoneDefaultMin, 10) || 5; maxD = parseInt(c.dataset.zoneDefaultMax, 10) || 8;
      }
      if (maxD < minD) maxD = minD;
      const today = new Date();
      const minDate = new Date(today); minDate.setDate(minDate.getDate() + minD);
      const maxDate = new Date(today); maxDate.setDate(maxDate.getDate() + maxD);
      return { pincode, minDate, maxDate, dispatchText: c.dataset.dispatchText || 'Dispatch in 2 hours' };
    }

    _formatDate(date) {
      return date.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' });
    }

    _setPincodeLoading(show) {
      if (this.pincodeLoading) {
        if (show) {
          this.pincodeLoading.removeAttribute('hidden');
          if (this.pincodeResult) this.pincodeResult.setAttribute('hidden', '');
        } else {
          this.pincodeLoading.setAttribute('hidden', '');
        }
      }
      if (this.pincodeBtn) this.pincodeBtn.disabled = show;
    }

    _showPincodeSuccess(result, pincode) {
      if (!this.pincodeResult) return;
      this.pincodeResult.innerHTML = `<div class="hulasi-pincode__result--success">
        <div class="hulasi-pincode__result-row"><span class="hulasi-pincode__result-icon hulasi-pincode__result-icon--success" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.3 4.3L6 11.6 2.7 8.3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span><span class="hulasi-pincode__result-text hulasi-pincode__result-text--success">Delivery available to <strong>${pincode}</strong> <button type="button" class="hulasi-pincode__change-link" id="HulasiPincodeChange-${this.sectionId}">Change</button></span></div>
        <div class="hulasi-pincode__result-row"><span class="hulasi-pincode__result-icon hulasi-pincode__result-icon--dispatch" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><path d="M8 4.5V8l2.5 1.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg></span><span class="hulasi-pincode__result-text"><strong>${result.dispatchText}</strong></span></div>
        <div class="hulasi-pincode__result-row"><span class="hulasi-pincode__result-icon hulasi-pincode__result-icon--delivery" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="3.5" width="10" height="7" rx="1" stroke="currentColor" stroke-width="1.2"/><path d="M11 6h2.5l1.5 2.5V10.5h-4V6z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="4.5" cy="12" r="1.5" stroke="currentColor" stroke-width="1.2"/><circle cx="12.5" cy="12" r="1.5" stroke="currentColor" stroke-width="1.2"/></svg></span><span class="hulasi-pincode__result-text">Estimated delivery: <span class="hulasi-pincode__date">${this._formatDate(result.minDate)}</span> – <span class="hulasi-pincode__date">${this._formatDate(result.maxDate)}</span></span></div>
      </div>`;
      this.pincodeResult.removeAttribute('hidden');
      const changeLink = this.pincodeResult.querySelector(`#HulasiPincodeChange-${this.sectionId}`);
      if (changeLink) changeLink.addEventListener('click', () => this._resetPincode());
    }

    _showPincodeTypicalEstimate(pincode) {
      if (!this.pincodeResult) return;
      this.pincodeResult.innerHTML = `<div class="hulasi-pincode__result--success">
        <div class="hulasi-pincode__result-row"><span class="hulasi-pincode__result-text hulasi-pincode__result-text--success">Indian pincode <strong>${pincode}</strong> verified. <button type="button" class="hulasi-pincode__change-link" id="HulasiPincodeChange-${this.sectionId}">Change</button></span></div>
        <div class="hulasi-pincode__result-row"><span class="hulasi-pincode__result-text">Usually packed within <strong>2 hours</strong>.</span></div>
        <div class="hulasi-pincode__result-row"><span class="hulasi-pincode__result-text">Typical delivery in <strong>4–5 days</strong>, subject to courier serviceability.</span></div>
      </div>`;
      this.pincodeResult.removeAttribute('hidden');
      const changeLink = this.pincodeResult.querySelector(`#HulasiPincodeChange-${this.sectionId}`);
      if (changeLink) changeLink.addEventListener('click', () => this._resetPincode());
    }

    _showPincodeError(message) {
      if (!this.pincodeResult) return;
      this.pincodeResult.innerHTML = `<div class="hulasi-pincode__result-row"><span class="hulasi-pincode__result-icon hulasi-pincode__result-icon--error" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><path d="M8 5v3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="8" cy="11" r="0.75" fill="currentColor"/></svg></span><span class="hulasi-pincode__result-text hulasi-pincode__result-text--error">${message}</span></div>`;
      this.pincodeResult.removeAttribute('hidden');
    }

    _resetPincode() {
      if (this.pincodeInput) {
        this.pincodeInput.value = '';
        this.pincodeInput.classList.remove('hulasi-pincode__input--error');
        this.pincodeInput.focus();
      }
      if (this.pincodeResult) {
        this.pincodeResult.innerHTML = '';
        this.pincodeResult.setAttribute('hidden', '');
      }
    }

    _getPincodeCache() {
      try {
        const raw = localStorage.getItem(this._pincodeCacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.expiry && Date.now() > parsed.expiry) {
          localStorage.removeItem(this._pincodeCacheKey);
          return null;
        }
        return parsed.data || null;
      } catch (e) { return null; }
    }

    _setPincodeCache(data) {
      try {
        localStorage.setItem(this._pincodeCacheKey, JSON.stringify({ data, expiry: Date.now() + 86400000 }));
      } catch (e) { /* ignore */ }
    }

    _getSavedPincode() {
      try { return localStorage.getItem('hulasi-user-pincode') || ''; } catch (e) { return ''; }
    }

    _savePincode(pincode) {
      try { localStorage.setItem('hulasi-user-pincode', pincode); } catch (e) { /* ignore */ }
    }

    /* ===========================================================
       OFFERS ACCORDION
       =========================================================== */

    _initOffersAccordion() {
      const allDetails = this.section.querySelectorAll('.hulasi-offers__details, .hulasi-product__description-details');
      allDetails.forEach((details) => {
        const summary = details.querySelector('summary');
        const content = details.querySelector('.hulasi-offers__content, .hulasi-product__description-content');
        if (!summary || !content) return;
        let isAnimating = false;

        summary.addEventListener('click', (e) => {
          e.preventDefault();
          if (isAnimating) return;
          isAnimating = true;

          if (details.open) {
            const startHeight = content.scrollHeight;
            content.style.maxHeight = startHeight + 'px';
            content.style.overflow = 'hidden';
            requestAnimationFrame(() => {
              content.style.transition = 'max-height 0.3s ease, opacity 0.2s ease';
              content.style.maxHeight = '0px';
              content.style.opacity = '0';
            });
            const onClose = () => {
              details.open = false;
              content.style.maxHeight = '';
              content.style.opacity = '';
              content.style.overflow = '';
              content.style.transition = '';
              isAnimating = false;
              content.removeEventListener('transitionend', onClose);
            };
            content.addEventListener('transitionend', onClose, { once: true });
            setTimeout(() => { if (isAnimating) onClose(); }, 400);
          } else {
            details.open = true;
            const targetHeight = content.scrollHeight;
            content.style.maxHeight = '0px';
            content.style.opacity = '0';
            content.style.overflow = 'hidden';
            requestAnimationFrame(() => {
              content.style.transition = 'max-height 0.35s ease, opacity 0.25s ease';
              content.style.maxHeight = targetHeight + 'px';
              content.style.opacity = '1';
            });
            const onOpen = () => {
              content.style.maxHeight = '';
              content.style.overflow = '';
              content.style.transition = '';
              isAnimating = false;
              content.removeEventListener('transitionend', onOpen);
            };
            content.addEventListener('transitionend', onOpen, { once: true });
            setTimeout(() => { if (isAnimating) onOpen(); }, 450);
          }
        });
      });
    }
  }

  /* ===========================================================
     BOOTSTRAP
     =========================================================== */

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
      if (!el._hulasiInit) el._hulasiInit = new HulasiProduct(el);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', (e) => {
    HulasiReveal.cleanup(e.target);

    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) section._hulasiInit = new HulasiProduct(section);
    const similarSection = e.target.querySelector('.hulasi-similar');
    if (similarSection) HulasiSimilarProducts.init(similarSection);

    HulasiReveal.init(e.target);
  });

  /* ===========================================================
     SIMILAR PRODUCTS
     =========================================================== */

  const HulasiSimilarProducts = {
    init(sectionEl) {
      if (!sectionEl) return;
      const container = sectionEl.querySelector('[id^="HulasiSimilarContainer"]');
      const url = sectionEl.dataset.url;
      if (!container || !url) return;
      const track = container.querySelector('[id^="HulasiSimilarTrack"]');
      if (track && track.children.length > 0) { this._initSlider(sectionEl); return; }
      this._fetchRecommendations(sectionEl, container, url);
    },

    async _fetchRecommendations(sectionEl, container, url) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContainer = doc.querySelector('[id^="HulasiSimilarContainer"]');
        if (newContainer && newContainer.innerHTML.trim()) {
          container.innerHTML = newContainer.innerHTML;
          this._initSlider(sectionEl);
          HulasiReveal.init(sectionEl);
        } else {
          sectionEl.style.display = 'none';
        }
      } catch (err) {
        console.warn('[Hulasi Similar]', err);
        sectionEl.style.display = 'none';
      }
    },

    _initSlider(sectionEl) {
      const sectionId = sectionEl.dataset.sectionId;
      const track = sectionEl.querySelector(`#HulasiSimilarTrack-${sectionId}`);
      const prevBtn = sectionEl.querySelector(`#HulasiSimilarPrev-${sectionId}`);
      const nextBtn = sectionEl.querySelector(`#HulasiSimilarNext-${sectionId}`);
      const dotsContainer = sectionEl.querySelector(`#HulasiSimilarDots-${sectionId}`);
      if (!track || window.innerWidth >= 990) return;

      const cards = track.querySelectorAll('.hulasi-similar__card');
      if (cards.length <= 2) return;

      const perView = 2;
      const totalPages = Math.ceil(cards.length / perView);
      let currentPage = 0;

      if (dotsContainer) {
        dotsContainer.innerHTML = '';
        for (let i = 0; i < totalPages; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = `hulasi-similar__slider-dot${i === 0 ? ' hulasi-similar__slider-dot--active' : ''}`;
          dot.setAttribute('aria-label', `Page ${i + 1}`);
          dot.addEventListener('click', () => goToPage(i));
          dotsContainer.appendChild(dot);
        }
      }

      function goToPage(page) {
        if (page < 0 || page >= totalPages) return;
        currentPage = page;
        const cardWidth = cards[0].offsetWidth + 12;
        track.scrollTo({ left: page * perView * cardWidth, behavior: 'smooth' });
        updateControls();
      }

      function updateControls() {
        if (prevBtn) prevBtn.disabled = currentPage === 0;
        if (nextBtn) nextBtn.disabled = currentPage >= totalPages - 1;
        if (dotsContainer) {
          dotsContainer.querySelectorAll('.hulasi-similar__slider-dot').forEach((dot, i) => {
            dot.classList.toggle('hulasi-similar__slider-dot--active', i === currentPage);
          });
        }
      }

      if (prevBtn) prevBtn.addEventListener('click', () => goToPage(currentPage - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goToPage(currentPage + 1));

      let scrollTimeout;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (!cards[0]) return;
          const cardWidth = cards[0].offsetWidth + 12;
          const newPage = Math.round(track.scrollLeft / (perView * cardWidth));
          if (newPage !== currentPage) {
            currentPage = Math.min(newPage, totalPages - 1);
            updateControls();
          }
        }, 100);
      }, { passive: true });

      updateControls();
    }
  };

  function initSimilarProducts() {
    document.querySelectorAll('.hulasi-similar').forEach((el) => HulasiSimilarProducts.init(el));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSimilarProducts);
  else initSimilarProducts();

  /* Global reveal pass — catches non-product sections (similar, reviews)
     that were not processed by HulasiProduct._initReveal(). Safe to
     call multiple times; already-processed elements are skipped. */
  function initReveals() {
    HulasiReveal.init(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReveals);
  else initReveals();

})();
